
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserPets, getPetById, createPet, updatePet, deletePet } from "../services/petsService";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export function usePets() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const petsQuery = useQuery({
    queryKey: ['pets'],
    queryFn: async () => {
      console.log('🔍 usePets - Starting getUserPets query...');
      const result = await getUserPets();
      console.log('🔍 usePets - Query result:', result);
      console.log('🔍 usePets - Number of pets returned:', result?.length || 0);
      return result;
    },
    enabled: !!user,
  });

  console.log('🔍 usePets - Hook state:', {
    isLoading: petsQuery.isLoading,
    isError: petsQuery.isError,
    error: petsQuery.error,
    dataLength: petsQuery.data?.length || 0,
    data: petsQuery.data
  });

  const createPetMutation = useMutation({
    mutationFn: createPet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pets'] });
      toast.success("Pet created successfully!");
      // Also invalidate the individual pet query if it exists
      if (data && data.id) {
        queryClient.invalidateQueries({ queryKey: ['pet', data.id] });
      }
    },
    onError: (error: Error) => {
      console.error("Pet creation error:", error);
      toast.error(`Failed to create pet: ${error.message}`);
    }
  });

  const updatePetMutation = useMutation({
    mutationFn: ({ petId, petData }: { petId: string, petData: any }) => 
      updatePet(petId, petData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pets'] });
      toast.success("Pet updated successfully!");
      // Also invalidate the individual pet query
      if (data && data.id) {
        queryClient.invalidateQueries({ queryKey: ['pet', data.id] });
      }
    },
    onError: (error: Error) => {
      console.error("Pet update error:", error);
      toast.error(`Failed to update pet: ${error.message}`);
    }
  });

  const deletePetMutation = useMutation({
    mutationFn: deletePet,
    onSuccess: (_, petId) => {
      console.log('🗑️ usePets - Delete mutation success, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['pets'] });
      queryClient.removeQueries({ queryKey: ['pet', petId] });
      toast.success("Mascota eliminada exitosamente");
    },
    onError: (error: Error) => {
      console.error("🗑️ usePets - Delete mutation error:", error);
      toast.error(`Error al eliminar mascota: ${error.message}`);
    }
  });

  return {
    pets: petsQuery.data ?? [],
    isLoading: petsQuery.isLoading,
    error: petsQuery.error,
    createPet: createPetMutation.mutate,
    updatePet: updatePetMutation.mutate,
    deletePet: (petId: string, options?: { onSuccess?: () => void; onError?: (error?: Error) => void }) => {
      console.log('🗑️ usePets - deletePet called with petId:', petId);
      deletePetMutation.mutate(petId, {
        onSuccess: () => {
          console.log('🗑️ usePets - Delete success callback');
          options?.onSuccess?.();
        },
        onError: (error: any) => {
          console.error('🗑️ usePets - Delete error callback:', error);
          options?.onError?.(error);
        }
      });
    },
    isCreating: createPetMutation.isPending,
    isUpdating: updatePetMutation.isPending,
    isDeleting: deletePetMutation.isPending,
  };
}

export function usePet(petId: string | undefined) {
  return useQuery({
    queryKey: ['pet', petId],
    queryFn: () => getPetById(petId!),
    enabled: !!petId,
  });
}
